import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Repeat } from "lucide-react";

const DAY_LABELS = [
  { iso: 1, short: "Ma" },
  { iso: 2, short: "Ti" },
  { iso: 3, short: "On" },
  { iso: 4, short: "To" },
  { iso: 5, short: "Fr" },
  { iso: 6, short: "Lø" },
  { iso: 7, short: "Sø" },
];

export interface RecurrenceInputProps {
  enabled: boolean;
  onEnabledChange: (on: boolean) => void;
  type: "daily" | "weekly";
  onTypeChange: (type: "daily" | "weekly") => void;
  weeklyDays: number[];
  onWeeklyDaysChange: (days: number[]) => void;
  showDateRange?: boolean;
  startDate?: string;
  onStartDateChange?: (date: string) => void;
  hasEndDate?: boolean;
  onHasEndDateChange?: (has: boolean) => void;
  endDate?: string;
  onEndDateChange?: (date: string) => void;
}

export function RecurrenceInput({
  enabled,
  onEnabledChange,
  type,
  onTypeChange,
  weeklyDays,
  onWeeklyDaysChange,
  showDateRange = false,
  startDate,
  onStartDateChange,
  hasEndDate,
  onHasEndDateChange,
  endDate,
  onEndDateChange,
}: RecurrenceInputProps) {
  function toggleDay(iso: number) {
    if (weeklyDays.includes(iso)) {
      onWeeklyDaysChange(weeklyDays.filter((d) => d !== iso));
    } else {
      onWeeklyDaysChange([...weeklyDays, iso]);
    }
  }

  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Repeat className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm">Gjenta</Label>
        </div>
        <Switch checked={enabled} onCheckedChange={onEnabledChange} />
      </div>

      {enabled && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              type="button"
              variant={type === "daily" ? "default" : "outline"}
              size="sm"
              className="flex-1 text-xs"
              onClick={() => onTypeChange("daily")}
            >
              Hver dag
            </Button>
            <Button
              type="button"
              variant={type === "weekly" ? "default" : "outline"}
              size="sm"
              className="flex-1 text-xs"
              onClick={() => onTypeChange("weekly")}
            >
              Bestemte dager
            </Button>
          </div>

          {type === "weekly" && (
            <div className="flex gap-1.5 justify-center">
              {DAY_LABELS.map((d) => (
                <button
                  key={d.iso}
                  type="button"
                  onClick={() => toggleDay(d.iso)}
                  className={`h-9 w-9 rounded-full text-xs font-medium transition-colors ${
                    weeklyDays.includes(d.iso)
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {d.short}
                </button>
              ))}
            </div>
          )}

          {showDateRange && (
            <div className="space-y-2 pt-1">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Startdato</p>
                <Input
                  type="date"
                  value={startDate ?? ""}
                  onChange={(e) => onStartDateChange?.(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="recurrence-end-date"
                  checked={hasEndDate ?? false}
                  onCheckedChange={(v) => onHasEndDateChange?.(v)}
                />
                <Label htmlFor="recurrence-end-date" className="text-xs text-muted-foreground">
                  Sett sluttdato
                </Label>
              </div>
              {hasEndDate && (
                <Input
                  type="date"
                  value={endDate ?? ""}
                  onChange={(e) => onEndDateChange?.(e.target.value)}
                  min={startDate}
                  className="text-sm"
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
